import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { VALID_CATEGORIES } from './widgetRegistry';
import LayoutDesigner from './LayoutDesigner';
import useConfirm from './useConfirm';

// Top-level layouts management surface mounted as a tab inside CaseTypeAdmin
// (or any workspace admin shell). Workspace admin/editor only — the resolver
// is open to all members but the list/CRUD requires editor+.

const styles = {
  shell:        { display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 },
  topbar:       { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderBottom: '1px solid var(--border-color)', background: 'var(--bg-secondary)', flexShrink: 0, gap: '12px', flexWrap: 'wrap' },
  filters:      { display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' },
  body:         { flex: 1, minHeight: 0, overflow: 'auto', padding: '14px' },
  table:        { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
  th:           { textAlign: 'left', padding: '8px 10px', borderBottom: '2px solid var(--border-color)', fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' },
  td:           { padding: '8px 10px', borderBottom: '1px solid var(--border-color)' },
  btnPrimary:   { background: 'var(--accent-color)', color: '#fff', border: 'none', borderRadius: '6px', padding: '7px 14px', cursor: 'pointer', fontWeight: 600, fontSize: '13px' },
  btnSecondary: { background: 'var(--bg-tertiary)', color: 'var(--text-primary)', border: '1px solid var(--border-color)', borderRadius: '6px', padding: '7px 14px', cursor: 'pointer', fontSize: '13px' },
  btnDanger:    { background: '#dc262620', color: '#ef4444', border: '1px solid #ef444450', borderRadius: '6px', padding: '7px 14px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 },
  input:        { padding: '6px 10px', borderRadius: '6px', border: '1px solid var(--border-color)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: '12px', outline: 'none' },
  empty:        { padding: '40px', textAlign: 'center', color: 'var(--text-secondary)' },
};

function authHeaders(token) { return { Authorization: `Bearer ${token}` }; }


export default function LayoutsList({ token, workspaceId, currentUser }) {
  const [layouts, setLayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [search, setSearch] = useState('');
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [openLayoutId, setOpenLayoutId] = useState(null);
  const { confirm, dialog: confirmDialog } = useConfirm();

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (categoryFilter) params.category = categoryFilter;
      const r = await axios.get(
        `${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/layouts`,
        { headers: authHeaders(token), params },
      );
      setLayouts(r.data || []);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load layouts.');
    } finally {
      setLoading(false);
    }
  }, [token, workspaceId, categoryFilter]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!search) return layouts;
    const q = search.toLowerCase();
    return layouts.filter(l => (l.name || '').toLowerCase().includes(q));
  }, [layouts, search]);

  const onCreate = useCallback(async (body) => {
    try {
      const r = await axios.post(
        `${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/layouts`,
        body,
        { headers: authHeaders(token) },
      );
      setShowNewDialog(false);
      await load();
      setOpenLayoutId(r.data.id);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Create failed');
    }
  }, [token, workspaceId, load]);

  const onDelete = useCallback(async (layoutId, layoutName) => {
    const ok = await confirm({
      title: 'Delete layout?',
      message: `"${layoutName}" will be removed. Bindings pointing at it will fall back to the built-in default until they're re-pointed.`,
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;
    try {
      await axios.delete(
        `${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/layouts/${layoutId}`,
        { headers: authHeaders(token) },
      );
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Delete failed');
    }
  }, [token, workspaceId, load, confirm]);

  const onDuplicate = useCallback(async (layoutId) => {
    try {
      const r = await axios.post(
        `${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/layouts/${layoutId}/duplicate`,
        {},
        { headers: authHeaders(token) },
      );
      await load();
      setOpenLayoutId(r.data.id);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Duplicate failed');
    }
  }, [token, workspaceId, load]);

  // ────────────────────────────────────────────────────────
  // Render
  // ────────────────────────────────────────────────────────

  if (openLayoutId) {
    return (
      <LayoutDesigner
        token={token}
        workspaceId={workspaceId}
        currentUser={currentUser}
        layoutId={openLayoutId}
        onBack={() => { setOpenLayoutId(null); load(); }}
      />
    );
  }

  return (
    <div style={styles.shell}>
      <div style={styles.topbar}>
        <div style={styles.filters}>
          <h3 style={{ margin: 0, fontSize: '15px' }}>Layouts</h3>
          <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} style={styles.input}>
            <option value="">All categories</option>
            {VALID_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <input
            type="text"
            placeholder="Search by name…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ ...styles.input, minWidth: '200px' }}
          />
        </div>
        <button onClick={() => setShowNewDialog(true)} style={styles.btnPrimary}>+ New layout</button>
      </div>

      {error && (
        <div style={{ padding: '8px 14px', background: '#ef444420', color: '#ef4444', fontSize: '12px', borderBottom: '1px solid #ef444440' }}>
          {error}
        </div>
      )}

      <div style={styles.body}>
        {loading && <div>Loading…</div>}
        {!loading && filtered.length === 0 && (
          <div style={styles.empty}>
            <div style={{ fontSize: '14px', marginBottom: '6px' }}>No layouts yet.</div>
            <div style={{ fontSize: '12px' }}>Click <strong>+ New layout</strong> to start. Workers continue to see the built-in defaults until you bind a published layout.</div>
          </div>
        )}
        {!loading && filtered.length > 0 && (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Name</th>
                <th style={styles.th}>Category</th>
                <th style={styles.th}>State</th>
                <th style={styles.th}>Last published</th>
                <th style={styles.th}>Lock</th>
                <th style={{ ...styles.th, width: '180px' }}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(l => (
                <tr key={l.id} style={{ cursor: 'pointer' }}>
                  <td style={styles.td} onClick={() => setOpenLayoutId(l.id)}>
                    <strong>{l.name}</strong>
                    {l.description && <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{l.description}</div>}
                  </td>
                  <td style={styles.td}>{l.category}</td>
                  <td style={styles.td}>
                    {l.has_unpublished_changes
                      ? <span style={{ color: '#f59e0b', fontWeight: 600 }}>● Unpublished changes</span>
                      : (l.has_published
                        ? <span style={{ color: '#10b981' }}>✓ Up to date</span>
                        : <span style={{ color: 'var(--text-secondary)' }}>Draft only</span>)}
                  </td>
                  <td style={styles.td}>
                    {l.published_at
                      ? <>v{l.published_version} · {new Date(l.published_at).toLocaleDateString()}</>
                      : <span style={{ color: 'var(--text-secondary)' }}>—</span>}
                  </td>
                  <td style={styles.td}>
                    {l.locked_by_user_id
                      ? <span style={{ color: '#f59e0b' }}>🔒 {l.locked_by_user_name || `user ${l.locked_by_user_id}`}</span>
                      : <span style={{ color: 'var(--text-secondary)' }}>—</span>}
                  </td>
                  <td style={styles.td}>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button onClick={() => setOpenLayoutId(l.id)} style={styles.btnSecondary}>Edit</button>
                      <button onClick={() => onDuplicate(l.id)} style={styles.btnSecondary}>Copy</button>
                      <button onClick={() => onDelete(l.id, l.name)} style={styles.btnDanger}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showNewDialog && (
        <NewLayoutDialog
          onCancel={() => setShowNewDialog(false)}
          onConfirm={onCreate}
        />
      )}
      {confirmDialog}
    </div>
  );
}


function NewLayoutDialog({ onCancel, onConfirm }) {
  const [category, setCategory] = useState('case');
  const [name, setName] = useState('');
  const [fromDefault, setFromDefault] = useState(true);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
      <div style={{ background: 'var(--bg-secondary)', padding: '20px', borderRadius: '8px', width: '440px', maxWidth: '95vw', border: '1px solid var(--border-color)' }}>
        <h3 style={{ margin: 0, fontSize: '15px' }}>New layout</h3>
        <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: '8px 0 14px' }}>
          Layouts are invisible to workers until you publish them. You can edit and preview the draft as long as you like.
        </p>
        <div style={{ marginBottom: '10px' }}>
          <label style={{ fontSize: '11px', textTransform: 'uppercase', color: 'var(--text-secondary)', fontWeight: 600 }}>Category</label>
          <select value={category} onChange={e => setCategory(e.target.value)} style={{ ...styles.input, width: '100%', marginTop: '4px' }}>
            {VALID_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div style={{ marginBottom: '10px' }}>
          <label style={{ fontSize: '11px', textTransform: 'uppercase', color: 'var(--text-secondary)', fontWeight: 600 }}>Name</label>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="e.g. Insurance Claim Detail"
            style={{ ...styles.input, width: '100%', marginTop: '4px', boxSizing: 'border-box' }}
          />
        </div>
        <div style={{ marginBottom: '10px' }}>
          <label style={{ fontSize: '11px', textTransform: 'uppercase', color: 'var(--text-secondary)', fontWeight: 600 }}>Starting point</label>
          <div style={{ marginTop: '6px', display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '12px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <input type="radio" checked={fromDefault} onChange={() => setFromDefault(true)} />
              Start from default <span style={{ color: 'var(--text-secondary)' }}>(clones the built-in {category} layout — recommended)</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <input type="radio" checked={!fromDefault} onChange={() => setFromDefault(false)} />
              Start blank <span style={{ color: 'var(--text-secondary)' }}>(empty stack root)</span>
            </label>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '14px' }}>
          <button onClick={onCancel} style={styles.btnSecondary}>Cancel</button>
          <button
            disabled={!name.trim()}
            onClick={() => onConfirm({ category, name: name.trim(), from_default: fromDefault })}
            style={{ ...styles.btnPrimary, opacity: name.trim() ? 1 : 0.5, cursor: name.trim() ? 'pointer' : 'not-allowed' }}
          >Create</button>
        </div>
      </div>
    </div>
  );
}
